const FwdSearchDirectory = (() => {
  let indexPromise;
  let countPromise;

  const STOREFRONT_ENDPOINT = '/api/2026-07/graphql.json';
  const COUNT_CACHE_TTL = 15 * 60 * 1000;
  const COUNT_CACHE_VERSION = 1;
  const FALLBACK_COUNT_CONCURRENCY = 1;
  const DIRECTORY_COUNT_QUERY = `
    query SearchDirectoryCounts($cursor: String, $country: CountryCode)
      @inContext(country: $country) {
      products(first: 250, after: $cursor, sortKey: ID) {
        nodes {
          id
          availableForSale
          brand: metafield(namespace: "custom", key: "brand") {
            type
            value
          }
          countries: metafield(namespace: "custom", key: "countries") {
            type
            value
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `;

  const normalize = (value) => String(value || '').normalize('NFKC').toLocaleLowerCase().trim();

  const uniqueByIdentity = (items) => Array.from(new Map(items.map((item) => [item.gid || item.url, item])).values());

  const uniqueByLabel = (items) => Array.from(new Map(items.map((item) => [normalize(item.label), item])).values());

  const emptyCounts = () => ({ brandIds: new Map(), brandUrls: new Map(), countryIds: new Map() });

  function incrementCount(map, key, available) {
    if (!key) return;
    const count = map.get(key) || { available: 0, total: 0 };
    count.total += 1;
    if (available) count.available += 1;
    map.set(key, count);
  }

  function metafieldIds(metafield) {
    if (!metafield || typeof metafield.value !== 'string') return [];
    if (metafield.type === 'metaobject_reference') {
      return metafield.value.startsWith('gid://shopify/Metaobject/') ? [metafield.value] : [];
    }
    if (metafield.type !== 'list.metaobject_reference') return [];

    try {
      const ids = JSON.parse(metafield.value);
      return Array.isArray(ids)
        ? ids.filter((id) => typeof id === 'string' && id.startsWith('gid://shopify/Metaobject/'))
        : [];
    } catch (_error) {
      return [];
    }
  }

  function countCacheKey() {
    const context = [
      window.Shopify?.theme?.id || 'theme',
      window.Shopify?.country || 'KR',
      window.Shopify?.currency?.active || 'currency',
      globalThis.document?.documentElement?.lang || 'language',
    ].map((value) => encodeURIComponent(String(value))).join(':');
    return `fwd:search-directory-counts:v${COUNT_CACHE_VERSION}:${context}`;
  }

  function serializeCounts(counts) {
    return {
      brandIds: Array.from(counts.brandIds.entries()),
      brandUrls: Array.from(counts.brandUrls.entries()),
      countryIds: Array.from(counts.countryIds.entries()),
    };
  }

  function validCountEntries(entries) {
    return Array.isArray(entries) && entries.every(([key, count]) => (
      typeof key === 'string'
      && Number.isInteger(count?.available)
      && Number.isInteger(count?.total)
      && count.available >= 0
      && count.total >= count.available
    ));
  }

  function readCachedCounts() {
    try {
      const cached = JSON.parse(window.sessionStorage.getItem(countCacheKey()) || 'null');
      if (!cached
        || Date.now() - cached.cachedAt > COUNT_CACHE_TTL
        || !validCountEntries(cached.brandIds)
        || !validCountEntries(cached.brandUrls)
        || !validCountEntries(cached.countryIds)) return null;
      return {
        brandIds: new Map(cached.brandIds),
        brandUrls: new Map(cached.brandUrls),
        countryIds: new Map(cached.countryIds),
      };
    } catch (_error) {
      return null;
    }
  }

  function writeCachedCounts(counts) {
    try {
      window.sessionStorage.setItem(countCacheKey(), JSON.stringify({
        cachedAt: Date.now(),
        ...serializeCounts(counts),
      }));
    } catch (_error) {
      // Search remains usable when storage is unavailable.
    }
  }

  function storefrontAccessToken() {
    const element = document.getElementById('shopify-features');
    const features = element?.textContent ? JSON.parse(element.textContent) : null;
    if (typeof features?.accessToken !== 'string' || !features.accessToken) {
      throw new Error('Shopify public Storefront token was not found');
    }
    return features.accessToken;
  }

  function challengeError(status) {
    const error = new Error(`Search directory challenge detected: ${status || 'cooldown'}`);
    error.challenge = true;
    error.retryable = false;
    return error;
  }

  function responseHasImmediateChallenge(response) {
    return window.FwdCatalogCountGuard?.responseHasImmediateChallenge?.(response)
      ?? (response?.headers?.get?.('cf-mitigated') === 'challenge' || [403, 429].includes(response?.status));
  }

  async function responseBodyMatchesChallenge(response) {
    if (!response || typeof response.text !== 'function') return false;
    try {
      const body = await response.text();
      return window.FwdCatalogCountGuard?.challengeBodyMatches?.(body)
        ?? /(?:\/cdn-cgi\/challenge-platform|cf-chl-|just a moment(?:\u2026|\.{3})?|checking your browser|verify you are human)/i.test(body);
    } catch (_error) {
      return false;
    }
  }

  function throwChallenge(status) {
    window.FwdCatalogCountGuard?.openChallengeCircuit?.();
    throw challengeError(status);
  }

  async function fetchJsonWithChallengeHandling(request, label) {
    let lastError;
    for (let attempt = 1; attempt <= 3; attempt += 1) {
      try {
        if (window.FwdCatalogCountGuard?.challengeIsActive?.()) throw challengeError();
        const response = await request();
        const inspectionResponse = typeof response?.clone === 'function' ? response.clone() : null;
        if (responseHasImmediateChallenge(response)) throwChallenge(response.status);
        if (!response.ok) {
          if (response.status === 503 && await responseBodyMatchesChallenge(inspectionResponse)) {
            throwChallenge(response.status);
          }
          const error = new Error(`${label}: ${response.status}`);
          error.retryable = response.status >= 500;
          throw error;
        }
        try {
          return await response.json();
        } catch (_error) {
          if (await responseBodyMatchesChallenge(inspectionResponse)) throwChallenge(response.status);
          const error = new TypeError(`${label}: response was not JSON`);
          error.retryable = false;
          throw error;
        }
      } catch (error) {
        lastError = error;
        if (error.retryable === false || attempt === 3) throw error;
        await new Promise((resolve) => window.setTimeout(resolve, 100 * attempt));
      }
    }
    throw lastError;
  }

  async function fetchStorefrontCountPage(cursor, token) {
    const payload = await fetchJsonWithChallengeHandling(() => (
      fetch(new URL(STOREFRONT_ENDPOINT, window.location.origin), {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({
          query: DIRECTORY_COUNT_QUERY,
          variables: { cursor, country: window.Shopify?.country || 'KR' },
        }),
      })
    ), 'Search directory counts failed');
    if (Array.isArray(payload?.errors) && payload.errors.length) {
      throw new Error('Search directory counts returned GraphQL errors');
    }
    const products = payload?.data?.products;
    if (!Array.isArray(products?.nodes)
      || typeof products?.pageInfo?.hasNextPage !== 'boolean'
      || (products.pageInfo.hasNextPage && typeof products.pageInfo.endCursor !== 'string')) {
      throw new Error('Search directory counts response was incomplete');
    }
    return products;
  }

  async function loadStorefrontCounts() {
    const token = storefrontAccessToken();
    const counts = emptyCounts();
    const productIds = new Set();
    const cursors = new Set();
    let cursor = null;

    for (let page = 0; page < 20; page += 1) {
      const products = await fetchStorefrontCountPage(cursor, token);
      products.nodes.forEach((product) => {
        if (typeof product?.id !== 'string' || productIds.has(product.id)) return;
        productIds.add(product.id);
        const available = Boolean(product.availableForSale);
        metafieldIds(product.brand).forEach((id) => incrementCount(counts.brandIds, id, available));
        new Set(metafieldIds(product.countries)).forEach((id) => incrementCount(counts.countryIds, id, available));
      });
      if (!products.pageInfo.hasNextPage) return counts;
      const nextCursor = products.pageInfo.endCursor;
      if (!nextCursor || cursors.has(nextCursor)) throw new Error('Search directory count cursor did not advance');
      cursors.add(nextCursor);
      cursor = nextCursor;
    }
    throw new Error('Search directory counts exceeded the page safety limit');
  }

  async function fetchFallbackCountPages(view) {
    const firstUrl = new URL('/collections/all', window.location.origin);
    firstUrl.searchParams.set('view', view);
    const fetchPage = (url) => fetchJsonWithChallengeHandling(
      () => fetch(`${url.pathname}${url.search}`, { credentials: 'same-origin' }),
      `${view} count request failed`
    );
    const first = await fetchPage(firstUrl);
    if (!Number.isInteger(first?.pages) || !Array.isArray(first?.products)) {
      throw new Error(`${view} count response was incomplete`);
    }
    const remaining = new Array(first.pages - 1);
    let nextPage = 2;
    const worker = async () => {
      while (nextPage <= first.pages) {
        const page = nextPage;
        nextPage += 1;
        const pageUrl = new URL(firstUrl);
        pageUrl.searchParams.set('page', String(page));
        remaining[page - 2] = await fetchPage(pageUrl);
      }
    };
    const workerCount = Math.min(FALLBACK_COUNT_CONCURRENCY, first.pages - 1);
    await Promise.all(Array.from({ length: workerCount }, () => worker()));
    return [first, ...remaining];
  }

  async function loadFallbackCounts() {
    const counts = emptyCounts();
    const [brandPages, countryPages] = await Promise.all([
      fetchFallbackCountPages('brand-counts'),
      fetchFallbackCountPages('country-counts'),
    ]);
    const brandProducts = new Set();
    brandPages.flatMap((page) => page.products).forEach((product) => {
      if (typeof product?.id !== 'string' || brandProducts.has(product.id) || !product.brandUrl) return;
      brandProducts.add(product.id);
      const path = new URL(product.brandUrl, window.location.origin).pathname;
      incrementCount(counts.brandUrls, path, Boolean(product.available));
    });
    const countryProducts = new Map();
    countryPages.flatMap((page) => page.products).forEach((product) => {
      if (typeof product?.id !== 'string') return;
      const seenCountries = countryProducts.get(product.id) || new Set();
      new Set(product.countryIds || []).forEach((id) => {
        if (seenCountries.has(id)) return;
        seenCountries.add(id);
        incrementCount(counts.countryIds, id, Boolean(product.available));
      });
      countryProducts.set(product.id, seenCountries);
    });
    return counts;
  }

  function loadCounts() {
    if (countPromise) return countPromise;
    const cached = readCachedCounts();
    if (cached) {
      countPromise = Promise.resolve(cached);
      return countPromise;
    }
    countPromise = loadStorefrontCounts()
      .catch((error) => {
        if (error?.challenge) throw error;
        return loadFallbackCounts();
      })
      .then((counts) => {
        writeCachedCounts(counts);
        return counts;
      })
      .catch(() => emptyCounts());
    return countPromise;
  }

  function attachCount(item, count) {
    if (!count) return item;
    return { ...item, availableCount: count.available, totalCount: count.total };
  }

  function labelFromBrandUrl(url) {
    const handle = decodeURIComponent(new URL(url, window.location.origin).pathname.split('/').pop() || '');
    return handle
      .split('-')
      .filter(Boolean)
      .map((word) => word.charAt(0).toLocaleUpperCase() + word.slice(1))
      .join(' ');
  }

  function displayLabel(item) {
    return item.displayLabel || item.label;
  }

  function highlightParts(value, term) {
    const text = String(value || '');
    const query = String(term || '').trim();
    if (!query) return [{ text, highlighted: false }];
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return text
      .split(new RegExp(`(${escapedQuery})`, 'giu'))
      .map((part, index) => ({ text: part, highlighted: index % 2 === 1 }))
      .filter((part) => part.text !== '');
  }

  function highlightedTextMarkup(value, term, escapeHtml) {
    return highlightParts(value, term)
      .map((part) => part.highlighted
        ? `<strong class="highlight">${escapeHtml(part.text)}</strong>`
        : escapeHtml(part.text))
      .join('');
  }

  function markerImageMarkup(assetUrl, escapeHtml) {
    return `<img class="country-flag-image country-emoji brand-origin-flag__image" src="${escapeHtml(assetUrl)}" alt="" aria-hidden="true" width="18" height="18" loading="lazy">`;
  }

  function markerMarkup(item, escapeHtml) {
    if (!item.originEmoji && !item.markerAssetUrl) return '';
    const markerKind = item.markerKind || 'country';
    const markerLabel = escapeHtml(item.markerLabel || 'Brand marker');
    const markerCode = escapeHtml(item.markerCode || '');
    const dataAttribute = markerKind === 'country'
      ? `data-origin-country="${markerCode}"`
      : `data-brand-classification="${markerCode}"`;
    let markerContent = escapeHtml(item.originEmoji);
    if ((markerKind === 'country' || markerKind === 'region') && item.markerAssetUrl) {
      markerContent = markerImageMarkup(item.markerAssetUrl, escapeHtml);
      if (item.secondMarkerAssetUrl) {
        markerContent += `<span class="brand-origin-flag__separator" aria-hidden="true">/</span>${markerImageMarkup(item.secondMarkerAssetUrl, escapeHtml)}`;
      }
    }
    return `<span class="brand-origin-flag brand-marker brand-marker--${escapeHtml(markerKind)}" role="img" aria-label="${markerLabel}" title="${markerLabel}" ${dataAttribute}>${markerContent}</span>`;
  }

  function displayLabelMarkup(item, escapeHtml, term) {
    return `${markerMarkup(item, escapeHtml)}${highlightedTextMarkup(displayLabel(item), term, escapeHtml)}`;
  }

  function appendDisplayLabel(container, item, term) {
    if (item.originEmoji || item.markerAssetUrl) {
      const markerKind = item.markerKind || 'country';
      const marker = document.createElement('span');
      marker.className = `brand-origin-flag brand-marker brand-marker--${markerKind}`;
      marker.setAttribute('role', 'img');
      marker.setAttribute('aria-label', item.markerLabel || 'Brand marker');
      marker.title = item.markerLabel || '';
      if (markerKind === 'country') marker.dataset.originCountry = item.markerCode || '';
      else marker.dataset.brandClassification = item.markerCode || '';

      if ((markerKind === 'country' || markerKind === 'region') && item.markerAssetUrl) {
        const image = document.createElement('img');
        image.className = 'country-flag-image country-emoji brand-origin-flag__image';
        image.src = item.markerAssetUrl;
        image.alt = '';
        image.setAttribute('aria-hidden', 'true');
        image.width = 18;
        image.height = 18;
        image.loading = 'lazy';
        marker.append(image);
        if (item.secondMarkerAssetUrl) {
          const separator = document.createElement('span');
          separator.className = 'brand-origin-flag__separator';
          separator.setAttribute('aria-hidden', 'true');
          separator.textContent = '/';
          marker.append(separator);

          const secondImage = image.cloneNode();
          secondImage.src = item.secondMarkerAssetUrl;
          marker.append(secondImage);
        }
      } else {
        marker.textContent = item.originEmoji;
      }
      container.append(marker);
    }
    highlightParts(displayLabel(item), term).forEach((part) => {
      if (!part.highlighted) {
        container.append(document.createTextNode(part.text));
        return;
      }
      const highlight = document.createElement('strong');
      highlight.className = 'highlight';
      highlight.textContent = part.text;
      container.append(highlight);
    });
  }

  async function loadAllDirectoryPages(firstPayload) {
    const payloads = [firstPayload];
    const seenUrls = new Set();
    let nextUrl = firstPayload.nextUrl;

    while (nextUrl) {
      const url = new URL(nextUrl, window.location.origin);
      const requestUrl = `${url.pathname}${url.search}`;
      if (seenUrls.has(requestUrl)) throw new Error('Search directory pagination repeated a URL');
      seenUrls.add(requestUrl);

      const response = await fetch(requestUrl, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Search directory page request failed: ${response.status}`);
      const payload = await response.json();
      if (!Array.isArray(payload?.brands)) throw new Error('Search directory page was incomplete');
      payloads.push(payload);
      nextUrl = payload.nextUrl;
    }

    return payloads;
  }

  async function loadDirectoryIndex() {
    const directoryUrl = new URL('/pages/brands', window.location.origin);
    directoryUrl.searchParams.set('view', 'brand-directory-data');
    directoryUrl.searchParams.set('page', '1');
    const [directoryResponse, sitemapResponse, counts] = await Promise.all([
      fetch(`${directoryUrl.pathname}${directoryUrl.search}`, { credentials: 'same-origin' }),
      fetch('/sitemap_metaobject_pages_1.xml', { credentials: 'same-origin', cache: 'force-cache' }),
      loadCounts(),
    ]);
    if (!directoryResponse.ok || !sitemapResponse.ok) {
      throw new Error(`Search directory request failed: ${directoryResponse.status}/${sitemapResponse.status}`);
    }
    const [firstPayload, sitemapXml] = await Promise.all([directoryResponse.json(), sitemapResponse.text()]);
    if (!Array.isArray(firstPayload?.brands) || !Array.isArray(firstPayload?.countries)) {
      throw new Error('Search directory response was incomplete');
    }

    const directoryPayloads = await loadAllDirectoryPages(firstPayload);
    const directoryBrands = directoryPayloads.flatMap((payload) => payload.brands);

    const countedBrands = uniqueByIdentity(directoryBrands).map((brand) => {
      const pathname = new URL(brand.url, window.location.origin).pathname;
      return attachCount(brand, counts.brandIds.get(brand.gid) || counts.brandUrls.get(pathname));
    });
    const exactBrands = new Map(countedBrands.map((brand) => [
      new URL(brand.legacyUrl || brand.url, window.location.origin).pathname,
      brand,
    ]));
    const sitemap = new DOMParser().parseFromString(sitemapXml, 'application/xml');
    if (sitemap.querySelector('parsererror')) throw new Error('Search directory sitemap XML was invalid');
    const sitemapBrandUrls = Array.from(sitemap.querySelectorAll('url > loc'))
      .map((element) => element.textContent.trim())
      .filter((url) => new URL(url, window.location.origin).pathname.startsWith('/pages/brand/'));
    const brands = uniqueByLabel(sitemapBrandUrls.map((url) => {
      const pathname = new URL(url, window.location.origin).pathname;
      return exactBrands.get(pathname) || {
        label: labelFromBrandUrl(pathname),
        url: pathname,
        gid: `sitemap:${pathname}`,
      };
    }));

    const countries = uniqueByIdentity(firstPayload.countries)
      .map((country) => attachCount(country, counts.countryIds.get(country.gid)));
    return { brands, countries };
  }

  async function load() {
    if (indexPromise) return indexPromise;

    indexPromise = loadDirectoryIndex().catch((error) => {
      indexPromise = null;
      throw error;
    });

    return indexPromise;
  }

  async function search(term) {
    const query = normalize(term);
    if (!query) return { brands: [], countries: [] };
    const index = await load();
    const matches = (items, limit, dedupeByLabel = false) => {
      const filteredItems = items
      .filter((item) => normalize(item.label).includes(query))
      .sort((left, right) => normalize(left.label).localeCompare(normalize(right.label)));
      return (dedupeByLabel ? uniqueByLabel(filteredItems) : filteredItems).slice(0, limit);
    };
    return {
      brands: matches(index.brands, 6, true),
      countries: matches(index.countries, 6),
    };
  }

  return { appendDisplayLabel, displayLabel, displayLabelMarkup, load, search };
})();

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.loadingStateTemplate = this.predictiveSearchResults
      .querySelector('.predictive-search__loading-state')
      ?.cloneNode(true);
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.loadingMessages = window.FwdLoadingFeedback?.messages || [];
    this.loadingMessageIndex = -1;
    this.loadingMessageTimer = null;
    this.analyticsQueryTimer = null;
    this.searchUiOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('input', this.onRawInput.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('click', this.onResultClick.bind(this));
    this.modalDetails = this.closest('details');
    this.userTogglePending = false;
    const markUserToggle = () => { this.userTogglePending = true; };
    this.modalDetails?.querySelector('summary')?.addEventListener('click', markUserToggle);
    this.modalDetails?.querySelector('summary')?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') markUserToggle();
    });
    this.closest('.search-modal')?.querySelectorAll('.modal__close-button, .modal-overlay').forEach((element) => {
      element.addEventListener('click', markUserToggle);
    });
    this.modalDetails?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') markUserToggle();
    });
    this.modalDetails?.addEventListener('toggle', () => {
      const changed = this.searchUiOpen !== this.modalDetails.open;
      const userInitiated = this.userTogglePending;
      this.userTogglePending = false;
      this.searchUiOpen = this.modalDetails.open;
      if (!this.modalDetails.open) this.cancelQueryTracking();
      if (!changed || !userInitiated) return;
      this.isOpen = this.modalDetails.open;
      this.track(this.modalDetails.open ? 'Search Opened' : 'Search Closed', { surface: 'header' });
    });
  }

  track(eventName, properties) {
    try {
      window.fwdAnalytics?.track?.(eventName, properties);
    } catch (_error) {
      // Search must remain usable when analytics is unavailable.
    }
  }

  analyticsQuery() {
    return this.getQuery().slice(0, 100);
  }

  scheduleQueryTracking() {
    this.cancelQueryTracking();
    const query = this.analyticsQuery();
    if (query.length < 2) return;
    this.analyticsQueryTimer = window.setTimeout(() => {
      if (this.analyticsQuery() !== query) return;
      this.track('Search Query Changed', {
        surface: this.closest('.search-modal') ? 'header' : 'search_page',
        search_query: query,
        query_length: query.length,
      });
    }, 800);
  }

  cancelQueryTracking() {
    if (this.analyticsQueryTimer !== null && typeof window.clearTimeout === 'function') {
      window.clearTimeout(this.analyticsQueryTimer);
    }
    this.analyticsQueryTimer = null;
  }

  onRawInput() {
    this.scheduleQueryTracking();
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;
    if (!this.searchTerm.length) {
      this.abortController.abort();
      this.abortController = new AbortController();
      this.renderRecentlyViewedProducts();
      return;
    }

    this.recentlyViewedRenderId = (this.recentlyViewedRenderId || 0) + 1;

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    const query = this.analyticsQuery();
    if (query.length < 2 || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }
    this.track('Search Submitted', {
      surface: this.closest('.search-modal') ? 'header' : 'search_page',
      search_query: query,
      query_length: query.length,
    });
  }

  onFormReset(event) {
    this.cancelQueryTracking();
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
      this.renderRecentlyViewedProducts();
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.renderRecentlyViewedProducts();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.contains(document.activeElement)) return;

      const searchModalIsOpen = this.closest('details')?.hasAttribute('open');
      if (!this.getQuery().length && searchModalIsOpen) return;

      this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length && !this.hasAttribute('open')) this.renderRecentlyViewedProducts();
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  onResultClick(event) {
    const link = event.target.closest?.('[data-fwd-search-product-result]');
    if (!link || !this.contains(link)) return;
    const item = link.closest('li');
    const results = Array.from(this.querySelectorAll('.predictive-search__list-item'));
    const data = link.dataset || {};
    const query = this.analyticsQuery();
    if (query.length < 2) return;
    this.track('Search Result Selected', {
      surface: this.closest('.search-modal') ? 'header' : 'search_page',
      search_query: query,
      query_length: query.length,
      product_id: data.fwdProductId || data.productId,
      variant_id: data.fwdVariantId || data.variantId,
      product_category: data.fwdProductCategory,
      product_name: data.fwdProductName,
      brand: data.fwdBrand,
      price: data.fwdPrice,
      currency: data.fwdCurrency,
      list_name: data.fwdListName || 'search_results',
      list_position: data.fwdListPosition || (item ? results.indexOf(item) + 1 : undefined),
      placement: 'predictive_search',
    });
  }

  recentlyViewedMarkup(cards) {
    const listItems = cards.map((card, index) => `
      <li id="predictive-search-option-recent-${index + 1}" class="predictive-search__list-item" role="option" aria-selected="false">
        ${card}
      </li>`).join('');

    return `<div id="predictive-search-results" role="listbox" data-recently-viewed-search-results>
      <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions">
        <div class="predictive-search__result-group">
          <div>
            <h2 id="predictive-search-recent-products" class="predictive-search__heading text-body caption-with-letter-spacing">최근 조회 상품</h2>
            <ul class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-recent-products">
              ${listItems}
            </ul>
          </div>
        </div>
      </div>
      <span class="visually-hidden" data-predictive-search-live-region-count-value>${cards.length}개의 최근 조회 상품</span>
    </div>`;
  }

  async renderRecentlyViewedProducts() {
    const recentStore = window.FWDRecentlyViewed;
    if (!recentStore || this.getQuery().length) return;

    const limit = Math.max(1, Number.parseInt(this.dataset.recentlyViewedLimit, 10) || 6);
    const handles = recentStore.readHistory().slice(0, limit);
    if (!handles.length) {
      this.close();
      return;
    }

    const renderId = (this.recentlyViewedRenderId || 0) + 1;
    this.recentlyViewedRenderId = renderId;
    const results = await Promise.allSettled(
      handles.map((handle) => recentStore.fetchSearchCard(handle, this.dataset.rootUrl))
    );
    if (renderId !== this.recentlyViewedRenderId || this.getQuery().length) return;

    const cards = results
      .filter((result) => result.status === 'fulfilled')
      .map((result) => result.value);
    if (!cards.length) {
      this.close();
      return;
    }

    this.renderSearchResults(this.recentlyViewedMarkup(cards));
  }

  async getSearchResults(searchTerm) {
    const queryKey = FwdSearchQuery.cacheKey(searchTerm, 'predictive-product');
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    if (!this.isNativePredictiveSearchSupported()) {
      this.getFallbackSearchResults(searchTerm, queryKey);
      return;
    }

    const predictiveSearchUrl = new URL(routes.predictive_search_url, window.location.origin);
    FwdSearchQuery.configurePredictiveUrl(predictiveSearchUrl, searchTerm);
    predictiveSearchUrl.searchParams.set('section_id', 'predictive-search');

    try {
      const [response, directoryMatches] = await Promise.all([
        fetch(`${predictiveSearchUrl.pathname}${predictiveSearchUrl.search}`, {
          signal: this.abortController.signal,
        }),
        FwdSearchDirectory.search(searchTerm).catch(() => ({ brands: [], countries: [] })),
      ]);
      if (searchTerm !== this.searchTerm) return;
      if (response.status === 417) {
        this.getFallbackSearchResults(searchTerm, queryKey);
        return;
      }
      if (!response.ok) throw new Error(response.status);

      const text = await response.text();
      const section = new DOMParser()
        .parseFromString(text, 'text/html')
        .querySelector('#shopify-section-predictive-search');
      if (!section) throw new Error('Predictive search section was not rendered');
      const resultsMarkup = this.addDirectoryResults(section.innerHTML, directoryMatches);
      this.cacheAndRender(queryKey, resultsMarkup);
    } catch (error) {
      if (error?.code === 20 || error?.name === 'AbortError') return;
      if (searchTerm !== this.searchTerm) return;
      this.close();
      console.error('Predictive search failed:', error);
    }
  }

  isNativePredictiveSearchSupported() {
    const featuresElement = document.getElementById('shopify-features');

    if (!featuresElement?.textContent) return true;

    try {
      return JSON.parse(featuresElement.textContent).predictiveSearch !== false;
    } catch (_error) {
      return true;
    }
  }

  async getFallbackSearchResults(searchTerm, queryKey) {
    try {
      const [response, directoryMatches] = await Promise.all([
        fetch(this.getFallbackSearchUrl(searchTerm), { signal: this.abortController.signal }),
        FwdSearchDirectory.search(searchTerm).catch(() => ({ brands: [], countries: [] })),
      ]);
      if (searchTerm !== this.searchTerm) return;
      if (!response.ok) throw new Error(response.status);
      const text = await response.text();
      const resultsSection = new DOMParser()
        .parseFromString(text, 'text/html')
        .querySelector('#shopify-section-fwd-search-preview');
      if (!resultsSection) throw new Error('Fallback search preview section was not rendered');
      FwdSearchQuery.hydrate(resultsSection, searchTerm);
      const resultsMarkup = this.addDirectoryResults(resultsSection.innerHTML, directoryMatches);
      this.cacheAndRender(queryKey, resultsMarkup);
    } catch (error) {
      if (error?.code === 20 || error?.name === 'AbortError') return;
      if (searchTerm !== this.searchTerm) return;
      this.close();
      console.error('Search preview fallback failed:', error);
    }
  }

  cacheAndRender(queryKey, resultsMarkup) {
    this.allPredictiveSearchInstances.forEach((instance) => {
      instance.cachedResults[queryKey] = resultsMarkup;
    });
    this.renderSearchResults(resultsMarkup);
  }

  escapeHtml(value) {
    return String(value).replace(/[&<>'"]/g, (character) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
    })[character]);
  }

  directoryGroupMarkup(type, heading, items) {
    if (!items.length) return '';
    return `<div class="predictive-search__directory-section">
      <h2 id="predictive-search-${type}" class="predictive-search__heading text-body caption-with-letter-spacing">${heading}</h2>
      <ul class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-${type}">
        ${items.map((item, index) => {
          const hasCounts = Number.isInteger(item.availableCount) && Number.isInteger(item.totalCount);
          const counts = hasCounts
            ? `<span class="predictive-search__directory-counts">판매중 ${item.availableCount} / 전체 ${item.totalCount}</span>`
            : '';
          return `<li id="predictive-search-option-${type}-${index + 1}" class="predictive-search__list-item" role="option" aria-selected="false"><a href="${this.escapeHtml(item.url)}" class="predictive-search__item link link--text" tabindex="-1"><div class="predictive-search__item-content predictive-search__item-content--centered"><p class="predictive-search__item-heading predictive-search__directory-item-heading h5"><span>${FwdSearchDirectory.displayLabelMarkup(item, this.escapeHtml.bind(this), this.searchTerm)}</span>${counts}</p></div></a></li>`;
        }).join('')}
      </ul>
    </div>`;
  }

  addDirectoryResults(resultsMarkup, matches) {
    if (!matches.brands.length && !matches.countries.length) return resultsMarkup;
    const documentFragment = new DOMParser().parseFromString(`<div data-search-root>${resultsMarkup}</div>`, 'text/html');
    const root = documentFragment.querySelector('[data-search-root]');
    root.querySelector('[data-predictive-search-empty-state]')?.remove();
    let wrapper = root.querySelector('#predictive-search-results-groups-wrapper');
    if (!wrapper) {
      wrapper = documentFragment.createElement('div');
      wrapper.id = 'predictive-search-results-groups-wrapper';
      wrapper.className = 'predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-products';
      root.querySelector('#predictive-search-results')?.prepend(wrapper);
    }
    const group = documentFragment.createElement('div');
    group.className = 'predictive-search__result-group predictive-search__directory-group';
    group.innerHTML = this.directoryGroupMarkup('brands', 'BRANDS', matches.brands)
      + this.directoryGroupMarkup('countries', 'COUNTRIES', matches.countries);
    wrapper.prepend(group);
    const liveCount = root.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveCount) liveCount.textContent = `${matches.brands.length + matches.countries.length} directory results. ${liveCount.textContent}`;
    return root.innerHTML;
  }

  getFallbackSearchUrl(searchTerm) {
    return FwdSearchQuery.buildSearchUrl(searchTerm, {
      path: this.input.form.action,
      unavailableProducts: 'show',
      sectionId: 'fwd-search-preview',
    });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    if (!this.predictiveSearchResults.querySelector('.predictive-search__loading-state') && this.loadingStateTemplate) {
      this.predictiveSearchResults.append(this.loadingStateTemplate.cloneNode(true));
    }

    this.startLoadingMessages();
    this.setAttribute('loading', true);
  }

  startLoadingMessages() {
    this.stopLoadingMessages();
    this.loadingMessages = this.loadingMessages?.length ? this.loadingMessages : [this.loadingText];
    this.loadingMessageIndex = (this.loadingMessageIndex + 1) % this.loadingMessages.length;
    this.updateLoadingMessage();
    this.setLiveRegionText(this.loadingText);
    if (typeof window.setInterval === 'function') {
      this.loadingMessageTimer = window.setInterval(() => {
        this.loadingMessageIndex = (this.loadingMessageIndex + 1) % this.loadingMessages.length;
        this.updateLoadingMessage();
      }, 1400);
    }
  }

  updateLoadingMessage() {
    const message = this.loadingMessages[this.loadingMessageIndex] || this.loadingText;
    if (this.predictiveSearchResults.dataset) this.predictiveSearchResults.dataset.loadingMessage = message;
  }

  stopLoadingMessages() {
    if (this.loadingMessageTimer && typeof window.clearInterval === 'function') {
      window.clearInterval(this.loadingMessageTimer);
    }
    this.loadingMessageTimer = null;
  }

  setLiveRegionText(statusText) {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    if (!this.statusElement) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.stopLoadingMessages();
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    if (this.modalDetails && !this.modalDetails.open) return;
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    if (!this.modalDetails && !this.isOpen) {
      this.track('Search Opened', { surface: this.closest('.search-modal') ? 'header' : 'search_page' });
    }
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.cancelQueryTracking();
    this.closeResults(clearSearchTerm);
    if (!this.modalDetails && this.isOpen) {
      this.track('Search Closed', { surface: this.closest('.search-modal') ? 'header' : 'search_page' });
    }
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    this.stopLoadingMessages();
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);

async function renderSearchDirectoryResults() {
  const container = document.querySelector('[data-search-directory-results]');
  if (!container?.dataset.searchQuery) return;

  try {
    const matches = await FwdSearchDirectory.search(container.dataset.searchQuery);
    const resultCount = matches.brands.length + matches.countries.length;
    if (!resultCount) return;

    const renderGroup = (heading, items) => {
      if (!items.length) return;
      const section = document.createElement('section');
      section.className = 'search-directory-results__group';
      const title = document.createElement('h2');
      title.className = 'h3';
      title.textContent = heading;
      const list = document.createElement('ul');
      list.className = 'search-directory-results__list list-unstyled';
      items.forEach((item) => {
        const listItem = document.createElement('li');
        const link = document.createElement('a');
        link.className = 'search-directory-results__link link link--text';
        link.href = item.url;
        FwdSearchDirectory.appendDisplayLabel(link, item, container.dataset.searchQuery);
        if (Number.isInteger(item.availableCount) && Number.isInteger(item.totalCount)) {
          const counts = document.createElement('span');
          counts.className = 'search-directory-results__count';
          counts.textContent = `판매중 ${item.availableCount} / 전체 ${item.totalCount}`;
          link.append(counts);
        }
        listItem.append(link);
        list.append(listItem);
      });
      section.append(title, list);
      container.append(section);
    };

    renderGroup('BRANDS', matches.brands);
    renderGroup('COUNTRIES', matches.countries);
    container.hidden = false;
    document.querySelector('[data-search-native-empty]')?.setAttribute('hidden', '');
  } catch (error) {
    console.error('Full search directory results failed:', error);
  }
}

if (typeof document.querySelector === 'function') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', renderSearchDirectoryResults);
  } else {
    renderSearchDirectoryResults();
  }
}
